import type { Note, NoteOwnerType } from '@/types';

import {
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useGetNotesQuery,
  useUpdateNoteMutation,
} from '@/store/api/noteApi';

export const useNotes = (
  owneableId: string,
  owneableType?: NoteOwnerType,
  page = 1,
  limit = 10,
) => {
  const {
    data,
    isLoading: isNotesLoading,
    refetch: refetchNotes,
  } = useGetNotesQuery({ limit, owneableId, page, owneableType });

  return {
    data,
    isNotesLoading,
    refetchNotes,
  };
};

export const useCreateNote = () => {
  const [trigger, result] = useCreateNoteMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0],
      options?: {
        onSuccess?: (data: Note) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useEditNote = () => {
  const [trigger, result] = useUpdateNoteMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0],
      options?: {
        onSuccess?: (data: Note) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useDeleteNote = () => {
  const [trigger, result] = useDeleteNoteMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0],
      options?: {
        onSuccess?: (data: void) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};
